/**
 * clearOrphanedSections_v1.js
 *
 * FT-489: Self-healing one-time IDB migration to remove stale orphaned
 * section(pre_arrival) entries that permanently block the "Arrive" button.
 *
 * PROBLEM:
 *   On Android, the OS back-swipe exits the voyage screen without completing
 *   the pre-arrival checklist. This leaves a `section` entry with
 *   operationType === 'pre_arrival' in the voyage's unifiedLog that has no
 *   matching `operation_complete`. The `findUnmatchedSectionForStart` guard in
 *   useVoyages.js detects this orphan and refuses to start a new pre-arrival,
 *   permanently blocking the "Arrive" button with a silent scroll + toast and
 *   no recovery path.
 *
 * FIX:
 *   On startup, scan all operations_voyages records in IDB. For each voyage,
 *   identify any section entries where:
 *     (a) operationType === 'pre_arrival'
 *     (b) no matching operation_complete exists for the same operationId
 *     (c) the section entry is older than 5 minutes (not in-flight)
 *   Remove ALL unifiedLog entries sharing the orphaned operationId (section +
 *   any associated operation_data and check entries). Write the patched voyage
 *   back. Other voyages and all other fields are untouched.
 *
 * SCOPE:
 *   pre_arrival sections only; age guard prevents racing an in-progress
 *   checklist; completedIds guard prevents removing legitimately open sections.
 *
 * GUARD:
 *   localStorage flag sr.migration.clearOrphanedSections_v1 = 'complete'
 *   prevents re-runs. Written at the END, only on success. On error: flag is
 *   NOT set so the migration retries on next boot.
 *
 * PATTERN:
 *   Follows removeStuckVoyage_v3 (FT-484): localStorage flag guard, try/catch,
 *   reads/writes IDB (Dexie) storage table, console log on outcomes.
 *
 * CALLER:
 *   src/index.js — sequenced in the async IIFE AFTER removeStuckVoyage_v3,
 *   fire-and-forget after root.render() so React is already mounted before
 *   the write completes.
 */

import { db } from '../../lib/db';

const MIGRATION_FLAG = 'sr.migration.clearOrphanedSections_v1';
const ORPHAN_AGE_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Remove stale orphaned pre_arrival section entries from IDB.
 * @returns {Promise<boolean>} Resolves true if migration ran, false if skipped.
 */
export const clearOrphanedSections_v1 = async () => {
  // Guard: already done on this device.
  if (localStorage.getItem(MIGRATION_FLAG) === 'complete') {
    return false;
  }

  try {
    console.log('[FT-489] Starting orphaned pre_arrival section migration...');

    // Fetch all IDB records whose module is 'operations_voyages'.
    const voyageRecords = await db.storage
      .where('module')
      .equals('operations_voyages')
      .toArray();

    if (voyageRecords.length === 0) {
      console.log('[FT-489] No operations_voyages records in IDB — nothing to migrate.');
      localStorage.setItem(MIGRATION_FLAG, 'complete');
      return true;
    }

    let recordsChecked = 0;
    let totalOrphansRemoved = 0;

    for (const record of voyageRecords) {
      recordsChecked++;

      const voyages = record.value;
      if (!Array.isArray(voyages)) continue;

      let recordModified = false;
      const updatedVoyages = voyages.map(voyage => {
        if (!voyage || !Array.isArray(voyage.unifiedLog)) return voyage;

        const log = voyage.unifiedLog;
        const now = Date.now();

        // Build a Set of operationIds that have a matching operation_complete.
        const completedIds = new Set(
          log
            .filter(e => e && e.type === 'operation_complete' && e.operationId)
            .map(e => e.operationId)
        );

        // Find orphaned pre_arrival section entries:
        //   (a) type === 'section' with operationType === 'pre_arrival'
        //   (b) no matching operation_complete
        //   (c) older than 5 minutes (not in-flight)
        const orphanedIds = new Set(
          log
            .filter(e =>
              e &&
              e.type === 'section' &&
              e.operationType === 'pre_arrival' &&
              e.operationId &&
              !completedIds.has(e.operationId) &&
              now - new Date(e.timestamp).getTime() > ORPHAN_AGE_MS
            )
            .map(e => e.operationId)
        );

        if (orphanedIds.size === 0) return voyage;

        // Remove ALL log entries for the orphaned operationIds (section +
        // any associated operation_data / check entries).
        const patchedLog = log.filter(e => !e || !orphanedIds.has(e.operationId));

        console.log(
          `[FT-489] Voyage ${voyage.id}: removed ${log.length - patchedLog.length} entries ` +
          `for ${orphanedIds.size} orphaned pre_arrival operationId(s).`
        );

        totalOrphansRemoved += orphanedIds.size;
        recordModified = true;

        return {
          ...voyage,
          unifiedLog: patchedLog,
        };
      });

      if (recordModified) {
        await db.storage.put({
          ...record,
          value: updatedVoyages,
          timestamp: Date.now(),
        });
      }
    }

    // Mark complete — only on success.
    localStorage.setItem(MIGRATION_FLAG, 'complete');

    if (totalOrphansRemoved > 0) {
      console.log(
        `[FT-489] Migration complete: ${totalOrphansRemoved} orphaned pre_arrival section(s) ` +
        `removed across ${recordsChecked} IDB record(s).`
      );
    } else {
      console.log(
        `[FT-489] Migration complete: no orphaned sections found in ${recordsChecked} IDB record(s) — no-op.`
      );
    }

    return true;

  } catch (error) {
    console.error('[FT-489] Orphaned pre_arrival section migration failed:', error);
    // Do NOT set the flag — retry on next boot so the issue doesn't persist.
    return false;
  }
};
